import React, { useState, useEffect } from 'react';
import { Modal, Button, Card } from 'react-bootstrap';
import { getLoginReleaseNotes } from 'data/changelogData';

/**
 * "What's New" popup.
 *
 * By default it shows the release notes returned by the login API
 * (`release_notes`, stored by LoginForm/OtpForm). Pass `items` to render a
 * specific list instead (e.g. the changelog manager's live preview).
 */
const ChangelogPopUp = ({ show, onClose, onAccept, items }) => {
  const [loginReleaseNotes, setLoginReleaseNotes] = useState(getLoginReleaseNotes);

  // Re-read on open so a fresh login's notes are picked up.
  useEffect(() => {
    if (show && !items) {
      setLoginReleaseNotes(getLoginReleaseNotes());
    }
  }, [show, items]);

  const changelogs = Array.isArray(items) ? items : loginReleaseNotes;

  const handleClose = () => {
    if (onAccept) {
      onAccept();
    } else if (onClose) {
      onClose();
    }
  };

  const latestVersion = changelogs.length > 0 ? changelogs[0].version : '';

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
      className="changelog-modal"
    >
      <Modal.Header closeButton className="bg-light py-2">
        <Modal.Title as="h5" className="mb-0 d-flex align-items-center">
          <span className="fw-bold text-dark">What's New in Mapyourmeds</span>
          {latestVersion && (
            <span
              style={{
                backgroundColor: '#0d6efd',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: '0.85rem',
                padding: '3px 10px',
                borderRadius: '20px',
                marginLeft: '10px',
                display: 'inline-flex',
                alignItems: 'center',
                letterSpacing: '0.5px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}
            >
              {latestVersion}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <p className="text-600 fs-11 mb-3">
          Review the latest features, enhancements, and workflow improvements in Mapyourmeds.
        </p>

        {changelogs.length === 0 && (
          <p className="text-500 fs-11 mb-0">No release notes available.</p>
        )}

        <div className="changelog-list">
          {changelogs.map((item, index) => (
            <Card
              key={item.id || index}
              className={`mb-3 border rounded-3 shadow-none ${
                index === 0 ? 'border-primary-subtle bg-soft-primary-light' : 'border-200'
              }`}
            >
              <Card.Body className="p-3">
                {/* Header Row: Version Badge, Category Tag, Date */}
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                  <div className="d-flex align-items-center gap-2">
                    {item.version && (
                      <span
                        style={{
                          backgroundColor: '#1e293b',
                          color: '#ffffff',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          display: 'inline-block',
                          fontFamily: 'monospace'
                        }}
                      >
                        {item.version}
                      </span>
                    )}
                    {item.tag && (
                      <span
                        className={`badge bg-${item.tagVariant || 'primary'} fs-11 px-2 py-1`}
                        style={{ color: '#ffffff', backgroundColor: item.tagVariant === 'success' ? '#00d27a' : '#2c7be5' }}
                      >
                        {item.tag}
                      </span>
                    )}
                    {index === 0 && (
                      <span
                        className="badge bg-success text-white fs-11 px-2 py-1"
                        style={{ color: '#ffffff' }}
                      >
                        Newest
                      </span>
                    )}
                  </div>
                  {item.date && (
                    <small className="text-500 fw-semi-bold fs-11">
                      📅 {item.date}
                    </small>
                  )}
                </div>

                {item.title && (
                  <h6 className="fw-bold text-900 mb-1 fs-0 d-flex align-items-center flex-wrap">
                    <span>{item.title}</span>
                  </h6>
                )}

                {item.description && (
                  <p className="text-700 fs-11 mb-2 lh-base mt-1" style={{ whiteSpace: 'pre-line' }}>
                    {item.description}
                  </p>
                )}

                {item.highlights && item.highlights.length > 0 && (
                  <div className="bg-light p-2 rounded-2 mt-2 border border-200">
                    <ul className="mb-0 ps-3 fs-11 text-600">
                      {item.highlights.map((highlight, hIdx) => (
                        <li key={hIdx} className="mb-1">
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card.Body>
            </Card>
          ))}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={handleClose}>
          Got it
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangelogPopUp;
